import { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import FilterSetPreview, {
  type FilterChipItem,
  type LocalFilterSection,
} from '@/storybook/components/FilterSetPreview/FilterSetPreview';
import LoadingSpinner from '@/storybook/components/LoadingSpinner/LoadingSpinner';
import Modal from '@/storybook/components/Modal/Modal';
import {
  getActiveSectionKey,
  getActiveChannelKey,
} from '@/components/filter/SaveFiltersDialog/SaveFiltersDialog.utils';

import styles from './FilterPreviewModal.module.css';

export interface FilterPreviewData {
  dateRangeText?: string;
  globalFilterChips: FilterChipItem[];
  localFilterSections: LocalFilterSection[];
}

export interface FilterPreviewModalProps {
  'isOpen': boolean;
  'onClose': () => void;
  'title': string;
  'isLoading'?: boolean;
  'data': FilterPreviewData | null;
  'children'?: ReactNode;
  'data-testid'?: string;
}

const FilterPreviewModal: FC<FilterPreviewModalProps> = ({
  isOpen,
  onClose,
  title,
  isLoading = false,
  data,
  children,
  'data-testid': dataTestId = 'filter-preview-modal',
}) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} data-testid={dataTestId} className={styles.modal}>
      {children}
      {isLoading && (
        <div className={styles.loaderWrapper}>
          <LoadingSpinner />
        </div>
      )}
      {!isLoading && data && (
        <FilterSetPreview
          dateRangeLabel={t('global.filters.saveFiltersDialog.interactionDateRange')}
          dateRangeText={data.dateRangeText}
          globalFilterLabel={t('global.filters.saveFiltersDialog.filterHCPs')}
          globalFilterChips={data.globalFilterChips}
          localFilterSections={data.localFilterSections}
          activeSectionKey={getActiveSectionKey(pathname)}
          activeChannelKey={getActiveChannelKey(pathname)}
          inactiveLabel={String(t('global.filters.saveFiltersDialog.inactiveOnThisPage'))}
          data-testid={`${dataTestId}-preview`}
        />
      )}
    </Modal>
  );
};

export default FilterPreviewModal;
